import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import { DSCard, DSInput, DSSelect, DSButton } from '../design-system/primitives';
import './GiveBacks.css';

function GiveBacks() {
  const RUN_WINDOW_OPTIONS = [
    { value: 'previous_month', label: 'Previous month (default)' },
    { value: 'current_month_in_progress', label: 'Current month (in progress - testing)' }
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [perMachineAmountInput, setPerMachineAmountInput] = useState('10.00');
  const [hostPerEventAmountInput, setHostPerEventAmountInput] = useState('5.00');
  const [selectedOwners, setSelectedOwners] = useState({});
  const [selectedHosts, setSelectedHosts] = useState({});
  const [runWindow, setRunWindow] = useState('previous_month');
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }, []);

  const loadData = async (windowValue = runWindow) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getGiveBackPreview(windowValue);
      setPreview(response.data);
      setPerMachineAmountInput(Number(response.data.perMachineAmount || 10).toFixed(2));
      setHostPerEventAmountInput(Number(response.data.hostPerEventAmount || 5).toFixed(2));
      setRunWindow(response.data.runWindow || windowValue);

      const defaultSelections = {};
      (response.data.owners || []).forEach((owner) => {
        defaultSelections[owner.ownerUserId] = true;
      });
      setSelectedOwners(defaultSelections);

      const defaultHostSelections = {};
      (response.data.hosts || []).forEach((host) => {
        defaultHostSelections[host.hostUserId] = true;
      });
      setSelectedHosts(defaultHostSelections);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load give backs data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(runWindow);
    // Runs once for the initial load only; runWindow changes are already
    // handled explicitly by handleRunWindowChange calling loadData itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRunWindowChange = async (event) => {
    const nextRunWindow = event.target.value;
    setRunWindow(nextRunWindow);
    await loadData(nextRunWindow);
  };

  const formatCurrency = (value) => {
    return currencyFormatter.format(Number(value || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const selectedOwnerRows = useMemo(() => {
    if (!preview?.owners) return [];
    return preview.owners.filter((owner) => selectedOwners[owner.ownerUserId] !== false);
  }, [preview, selectedOwners]);

  const selectedHostRows = useMemo(() => {
    if (!preview?.hosts) return [];
    return preview.hosts.filter((host) => selectedHosts[host.hostUserId] !== false);
  }, [preview, selectedHosts]);

  const selectedTotals = useMemo(() => {
    const owners = selectedOwnerRows.length;
    const hosts = selectedHostRows.length;
    const actualMachines = selectedOwnerRows.reduce((sum, owner) => sum + Number(owner.actualMachineCount || 0), 0);
    const effectiveMachines = selectedOwnerRows.reduce((sum, owner) => sum + Number(owner.effectiveMachineCount || 0), 0);
    const hostedEvents = selectedHostRows.reduce((sum, host) => sum + Number(host.hostedEventCount || 0), 0);
    const ownerPayout = selectedOwnerRows.reduce((sum, owner) => sum + Number(owner.amount || 0), 0);
    const hostPayout = selectedHostRows.reduce((sum, host) => sum + Number(host.amount || 0), 0);
    const payout = ownerPayout + hostPayout;

    return {
      owners,
      hosts,
      actualMachines,
      effectiveMachines,
      hostedEvents,
      ownerPayout: Number(ownerPayout.toFixed(2)),
      hostPayout: Number(hostPayout.toFixed(2)),
      payout: Number(payout.toFixed(2))
    };
  }, [selectedOwnerRows, selectedHostRows]);

  const toggleOwnerSelection = (ownerUserId) => {
    setSelectedOwners((prev) => ({
      ...prev,
      [ownerUserId]: prev[ownerUserId] === false
    }));
  };

  const toggleHostSelection = (hostUserId) => {
    setSelectedHosts((prev) => ({
      ...prev,
      [hostUserId]: prev[hostUserId] === false
    }));
  };

  const selectAllOwners = () => {
    const nextSelections = {};
    (preview?.owners || []).forEach((owner) => {
      nextSelections[owner.ownerUserId] = true;
    });
    setSelectedOwners(nextSelections);
  };

  const unselectAllOwners = () => {
    const nextSelections = {};
    (preview?.owners || []).forEach((owner) => {
      nextSelections[owner.ownerUserId] = false;
    });
    setSelectedOwners(nextSelections);
  };

  const selectAllHosts = () => {
    const nextSelections = {};
    (preview?.hosts || []).forEach((host) => {
      nextSelections[host.hostUserId] = true;
    });
    setSelectedHosts(nextSelections);
  };

  const unselectAllHosts = () => {
    const nextSelections = {};
    (preview?.hosts || []).forEach((host) => {
      nextSelections[host.hostUserId] = false;
    });
    setSelectedHosts(nextSelections);
  };

  const handleSaveSettings = async () => {
    const parsedPerMachineAmount = parseFloat(perMachineAmountInput);
    const parsedHostPerEventAmount = parseFloat(hostPerEventAmountInput);

    if (isNaN(parsedPerMachineAmount) || parsedPerMachineAmount <= 0) {
      showToast('Please enter a valid per-machine amount greater than 0', 'error');
      return;
    }

    if (isNaN(parsedHostPerEventAmount) || parsedHostPerEventAmount <= 0) {
      showToast('Please enter a valid host per-event amount greater than 0', 'error');
      return;
    }

    try {
      setSaving(true);
      await apiService.updateGiveBackSettings({
        perMachineAmount: parsedPerMachineAmount,
        hostPerEventAmount: parsedHostPerEventAmount
      });
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update amount', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDistribute = () => {
    if (!preview) {
      return;
    }

    if (preview.alreadyRunForPeriod) {
      showToast('Give backs have already been distributed for this month window.', 'warning');
      return;
    }

    const hasEligibleOwners = preview.owners && preview.owners.length > 0;
    const hasEligibleHosts = preview.hosts && preview.hosts.length > 0;

    if (!hasEligibleOwners && !hasEligibleHosts) {
      showToast('No eligible owners or hosts found for distribution.', 'warning');
      return;
    }

    if (selectedOwnerRows.length === 0 && selectedHostRows.length === 0) {
      showToast('Please leave at least one owner or host checked for distribution.', 'warning');
      return;
    }

    setConfirmState({
      title: 'Distribute Give Backs?',
      message: `Distribute ${formatCurrency(selectedTotals.payout)} for ${preview.periodStart} to ${preview.periodEnd}? Owners: ${selectedTotals.owners} (${formatCurrency(selectedTotals.ownerPayout)}), Hosts: ${selectedTotals.hosts} (${formatCurrency(selectedTotals.hostPayout)}).`,
      confirmLabel: 'Distribute',
      danger: false,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          setDistributing(true);
          setError('');
          await apiService.distributeGiveBacks({
            selectedOwnerUserIds: selectedOwnerRows.map((owner) => owner.ownerUserId),
            selectedHostUserIds: selectedHostRows.map((host) => host.hostUserId),
            runWindow
          });
          await loadData();
          setConfirmState(null);
          showToast('Give backs distributed successfully.', 'success');
        } catch (err) {
          if (err.response?.status === 409) {
            await loadData();
            setConfirmState(null);
            showToast('This period was already distributed. Data has been refreshed.', 'warning');
          } else {
            setError(err.response?.data?.error || 'Failed to distribute give backs');
            setConfirmState(null);
          }
        } finally {
          setDistributing(false);
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleResetCurrentPeriodRun = () => {
    if (!preview?.alreadyRunForPeriod) {
      showToast('No current period run exists to reset.', 'warning');
      return;
    }

    setConfirmState({
      title: 'Reset Current Period Run?',
      message: `Reset give backs for ${preview.periodStart} to ${preview.periodEnd}? This deletes the run record, related credits, and related give back notifications for that run.`,
      confirmLabel: 'Reset',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          setResetting(true);
          setError('');
          await apiService.resetCurrentPeriodGiveBackRun(runWindow);
          await loadData();
          setConfirmState(null);
          showToast('Current period give back run was reset successfully.', 'success');
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to reset current period give back run');
          setConfirmState(null);
        } finally {
          setResetting(false);
          setConfirmSubmitting(false);
        }
      }
    });
  };

  if (loading) {
    return <p className="givebacks-loading">Loading Give Backs...</p>;
  }

  return (
    <div className="givebacks-tab">
      {error && (
        <div className="givebacks-error-banner">
          {error}
        </div>
      )}

      <div className="card givebacks-summary-card">
        <div className="givebacks-summary-grid">
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Run window</span>
            <span className="givebacks-summary-value">
              {preview?.periodStart} to {preview?.periodEnd}
            </span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Total payout</span>
            <span className="givebacks-summary-value highlight">
              {formatCurrency(selectedTotals.payout)}
            </span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Owners (selected)</span>
            <span className="givebacks-summary-value">{selectedTotals.owners}</span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Hosts (selected)</span>
            <span className="givebacks-summary-value">{selectedTotals.hosts}</span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Machines (actual)</span>
            <span className="givebacks-summary-value">{selectedTotals.actualMachines}</span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Machines (effective)</span>
            <span className="givebacks-summary-value">{selectedTotals.effectiveMachines}</span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Hosted events (selected)</span>
            <span className="givebacks-summary-value">{selectedTotals.hostedEvents}</span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Last distribution</span>
            <span className="givebacks-summary-value">
              {formatDate(preview?.lastRun?.executed_at)}
            </span>
          </div>
          <div className="givebacks-summary-item">
            <span className="givebacks-summary-label">Status</span>
            <span className={`givebacks-summary-value ${preview?.alreadyRunForPeriod ? 'status-done' : 'status-ready'}`}>
              {preview?.alreadyRunForPeriod ? 'Already run this month' : 'Ready to distribute'}
            </span>
          </div>
        </div>

        <div className="givebacks-controls">
          <div className="givebacks-amount-control">
            <DSSelect
              id="runWindow"
              label="Run window"
              value={runWindow}
              onChange={handleRunWindowChange}
              disabled={saving || distributing || resetting}
            >
              {RUN_WINDOW_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DSSelect>

            <DSInput
              id="perMachineAmount"
              label="Per machine amount ($)"
              type="number"
              min="0.01"
              step="0.01"
              value={perMachineAmountInput}
              onChange={(event) => setPerMachineAmountInput(event.target.value)}
              disabled={saving || distributing}
            />
            <DSInput
              id="hostPerEventAmount"
              label="Host per event amount ($)"
              type="number"
              min="0.01"
              step="0.01"
              value={hostPerEventAmountInput}
              onChange={(event) => setHostPerEventAmountInput(event.target.value)}
              disabled={saving || distributing}
            />
            <DSButton tone="secondary" appearance="solid" onClick={handleSaveSettings} disabled={saving || distributing}>
              {saving ? 'Saving...' : 'Save amount'}
            </DSButton>
          </div>

          <DSButton tone="primary" appearance="solid" onClick={handleDistribute} disabled={ distributing || resetting || saving || preview?.alreadyRunForPeriod || ((preview?.owners || []).length === 0 && (preview?.hosts || []).length === 0) || (selectedOwnerRows.length === 0 && selectedHostRows.length === 0) }>
            {distributing ? 'Distributing...' : 'Distribute monthly give backs'}
          </DSButton>

          {preview?.alreadyRunForPeriod && (
            <DSButton tone="danger" appearance="solid" onClick={handleResetCurrentPeriodRun} disabled={resetting || distributing || saving}>
              {resetting ? 'Resetting...' : 'Reset current month test run'}
            </DSButton>
          )}
        </div>
      </div>

      <div className="card givebacks-list-card">
        <h2>Owner breakdown</h2>

        {(!preview?.owners || preview.owners.length === 0) ? (
          <p className="givebacks-empty">No active machine owners found.</p>
        ) : (
          <>
            <div className="givebacks-bulk-actions">
              <DSButton tone="secondary" appearance="solid" onClick={selectAllOwners} disabled={distributing}>
                Select all
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={unselectAllOwners} disabled={distributing}>
                Unselect all
              </DSButton>
            </div>

            <div className="givebacks-owner-cards">
              {preview.owners.map((owner) => (
                <DSCard className="givebacks-owner-card" bgVariant="subtle" cornerVariant="md" key={owner.ownerUserId}>
                  <div className="givebacks-owner-row">
                    <label className="givebacks-owner-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedOwners[owner.ownerUserId] !== false}
                        onChange={() => toggleOwnerSelection(owner.ownerUserId)}
                        disabled={distributing}
                      />
                    </label>
                    <div className="givebacks-owner-name">{owner.ownerName}</div>
                  </div>
                  <div className="givebacks-owner-meta">
                    <span>
                      {owner.actualMachineCount} actual / {owner.effectiveMachineCount} effective
                    </span>
                    <strong>{formatCurrency(owner.amount)}</strong>
                  </div>
                </DSCard>
              ))}
            </div>

            <div className="givebacks-owner-table-wrap">
              <table className="givebacks-owner-table">
                <thead>
                  <tr>
                    <th>Include</th>
                    <th>Owner</th>
                    <th>Actual</th>
                    <th>Effective</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.owners.map((owner) => (
                    <tr key={`table-${owner.ownerUserId}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOwners[owner.ownerUserId] !== false}
                          onChange={() => toggleOwnerSelection(owner.ownerUserId)}
                          disabled={distributing}
                        />
                      </td>
                      <td>{owner.ownerName}</td>
                      <td>{owner.actualMachineCount}</td>
                      <td>{owner.effectiveMachineCount}</td>
                      <td>{formatCurrency(owner.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card givebacks-list-card">
        <h2>Host breakdown</h2>

        {(!preview?.hosts || preview.hosts.length === 0) ? (
          <p className="givebacks-empty">No eligible confirmed hosts found for this period.</p>
        ) : (
          <>
            <div className="givebacks-bulk-actions">
              <DSButton tone="secondary" appearance="solid" onClick={selectAllHosts} disabled={distributing}>
                Select all
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={unselectAllHosts} disabled={distributing}>
                Unselect all
              </DSButton>
            </div>

            <div className="givebacks-owner-cards">
              {preview.hosts.map((host) => (
                <DSCard className="givebacks-owner-card" bgVariant="subtle" cornerVariant="md" key={host.hostUserId}>
                  <div className="givebacks-owner-row">
                    <label className="givebacks-owner-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedHosts[host.hostUserId] !== false}
                        onChange={() => toggleHostSelection(host.hostUserId)}
                        disabled={distributing}
                      />
                    </label>
                    <div className="givebacks-owner-name">{host.hostName}</div>
                  </div>
                  <div className="givebacks-owner-meta">
                    <span>{host.hostedEventCount} hosted event(s)</span>
                    <strong>{formatCurrency(host.amount)}</strong>
                  </div>
                </DSCard>
              ))}
            </div>

            <div className="givebacks-owner-table-wrap">
              <table className="givebacks-owner-table">
                <thead>
                  <tr>
                    <th>Include</th>
                    <th>Host</th>
                    <th>Hosted events</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.hosts.map((host) => (
                    <tr key={`host-table-${host.hostUserId}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedHosts[host.hostUserId] !== false}
                          onChange={() => toggleHostSelection(host.hostUserId)}
                          disabled={distributing}
                        />
                      </td>
                      <td>{host.hostName}</td>
                      <td>{host.hostedEventCount}</td>
                      <td>{formatCurrency(host.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default GiveBacks;
